/**
 * Browser storage that belongs to ONE signed-in person (#2340).
 *
 * The React Query cache is scoped to one identity (#2330): `adoptUser` in
 * AuthContext drops it whenever the account or active role changes, on every
 * sign-in, on logout and on a mid-session 401. These keys hold the same kind
 * of per-person state outside that cache, so they are dropped at the same
 * moments by `clearUserScopedStorage()`. Without that, the next person on a
 * shared device resumed into the previous person's Williams or Stevens
 * thread, was offered their voice session, and saw their dashboard layout.
 *
 * Adding a per-user key is one line: name it here (the app-side owners import
 * their key from this file; the chat core cannot import app modules, so its
 * keys are exported by the core and listed here).
 *
 * Deliberately NOT here — device state, which belongs to the browser:
 * - auth tokens and the trusted-device token (`services/storage.ts`):
 *   `clearAuthTokens` owns the session tokens, and the trusted-device token is
 *   per device by design (#763).
 * - `trf.lang` (the signed-out visitor's language), `trf_theme`,
 *   `font-scale-preference`, sidebar mode/side/expanded, chat drawer width and
 *   recents view, collapse states (`trf:collapse:*`), calendar view and hidden
 *   categories, the medical tab, the Stevens widget open/expanded/speaker
 *   toggles, the machine-translation banner dismissal per language: display
 *   preferences of whoever uses this device, no data about the person.
 * - `trf:layout:*` widths: already keyed by user id (`lib/layout-prefs.ts`).
 * - the first-week welcome note's dismissal (`welcome_banner_dismissed_at:<id>`,
 *   WelcomeBanner.tsx): keyed by user id since #2405. It was listed here, and
 *   clearing it on every sign-in brought the banner back at the start of
 *   every session for the same person.
 * - the apply-flow sessionStorage keys (`trf-apply-*`, eligibility gate) and
 *   `mfa_onboarding_pending`: carried ACROSS the sign-in that ends signup /
 *   registration, so clearing on sign-in would break the flow they exist for.
 *   Logout already removes `mfa_onboarding_pending`.
 * - `impersonation_session`: AuthContext manages it directly.
 * - the copy cache (`useCopy`): org-wide wording, the same for everyone.
 * - `trf_chunk_reload_at`: a reload guard for the build, not a person.
 */
import { ACTIVE_KEY as WILLIAMS_ACTIVE_KEY, LAST_KEY as WILLIAMS_LAST_KEY } from '@/lib/williams-thread';
import {
  STEVENS_V4_CONVERSATION_KEY,
  STEVENS_V4_LEGACY_SESSION_KEY,
} from '@core/lib/localStorage';
import { VOICE_ACTIVE_SESSION_KEY } from '@core/voice/sessionResume';

/** Dashboard layout cache; the family profile is the source of truth. */
export const DASHBOARD_PREFS_KEY = 'trf_dashboard_prefs';
/**
 * When this person dismissed the first-week welcome note (WelcomeBanner).
 * Keyed by user id (#2405), the same pattern as the `trf:layout:*` widths, so
 * it never leaks between people and is deliberately NOT in the list below.
 */
export function welcomeBannerDismissedKey(userId: string): string {
  return `welcome_banner_dismissed_at:${userId}`;
}
/** This person's "don't ask again" on deleting a conversation. */
export const DELETE_CONV_SKIP_CONFIRM_KEY = 'trf_delete_conv_skip_confirm';
/** How often this person dismissed each health-page Stevens callout. */
export const STEVENS_CALLOUT_DISMISSES_KEY = 'trf:stevens-callout-dismisses';

export const USER_SCOPED_STORAGE_KEYS: readonly string[] = Object.freeze([
  WILLIAMS_ACTIVE_KEY,
  WILLIAMS_LAST_KEY,
  STEVENS_V4_CONVERSATION_KEY,
  STEVENS_V4_LEGACY_SESSION_KEY,
  VOICE_ACTIVE_SESSION_KEY,
  DASHBOARD_PREFS_KEY,
  DELETE_CONV_SKIP_CONFIRM_KEY,
  STEVENS_CALLOUT_DISMISSES_KEY,
]);

/**
 * Per-person state held in memory rather than in storage, outside the query
 * cache: a module that keeps any registers its reset here, so it is dropped at
 * the same moments as the keys above (sign-out, sign-in, a change of account
 * or role). The Committee page's page-wide AI runs (committee-editor/pageCheck.ts)
 * are the first (security review, 2026-10-27).
 */
const memoryResets = new Set<() => void>();
export function registerUserScopedReset(reset: () => void): void {
  memoryResets.add(reset);
}

/**
 * Remove every user-scoped key. Each removal is guarded on its own: Safari
 * private mode and some webviews throw on storage access, and a sign-out must
 * never fail because one key could not be removed.
 */
export function clearUserScopedStorage(): void {
  for (const reset of memoryResets) {
    try {
      reset();
    } catch {
      /* one store failing never keeps the others */
    }
  }
  for (const key of USER_SCOPED_STORAGE_KEYS) {
    try {
      localStorage.removeItem(key);
    } catch {
      /* storage unavailable — nothing persisted to leak */
    }
  }
}
